import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { format, type Locale } from "date-fns";
import { es, enUS, de, fr, it } from "date-fns/locale";
import esMessages, { type Messages } from "./messages/es";
import enMessages from "./messages/en";
import deMessages from "./messages/de";
import frMessages from "./messages/fr";
import itMessages from "./messages/it";

/**
 * Sistema de i18n propio y ligero para el widget (SIN i18next ni librerías
 * externas de traducción). Cubre únicamente `App.tsx` (flujo de mesas),
 * `pages/ReservationManage.tsx` y `pages/ConfirmReservation.tsx`.
 *
 * Fuera de alcance a propósito: `AppointmentsApp`/`AppointmentManage` (citas,
 * riesgo cero) y `Preferences`/`Unsubscribe` (emails de marketing, otra
 * fase) — esos ficheros NO importan nada de aquí y siguen en español fijo.
 * `AppDownloadCta.tsx` tampoco se tocó por ser compartido con
 * `AppointmentManage.tsx` (ver `.claude/memorias/frontend.notas.md`).
 */

export type Lang = "es" | "en" | "de" | "fr" | "it";

export const LANGS: ReadonlyArray<{ code: Lang; flag: string; label: string }> = [
  { code: "es", flag: "🇪🇸", label: "Español" },
  { code: "en", flag: "🇬🇧", label: "English" },
  { code: "de", flag: "🇩🇪", label: "Deutsch" },
  { code: "fr", flag: "🇫🇷", label: "Français" },
  { code: "it", flag: "🇮🇹", label: "Italiano" },
];

const STORAGE_KEY = "rm_widget_lang";

const messages: Record<Lang, Messages> = {
  es: esMessages,
  en: enMessages,
  de: deMessages,
  fr: frMessages,
  it: itMessages,
};

const dateLocales: Record<Lang, Locale> = { es, en: enUS, de, fr, it };

// Patrones de date-fns por idioma para las 5 formas de fecha que usa el
// widget. Cada idioma tiene su propia convención (comas, puntos, orden
// día/mes) en vez de reutilizar el patrón español con el locale cambiado.
interface DateFormatSet {
  /** "miércoles, 15 de enero" / "Wednesday, January 15" */
  long: string;
  /** "mié 15 ene" (resumen compacto) */
  short: string;
  /** "miércoles, 15 de enero de 2026" (con año) */
  full: string;
  /** "15 de enero" (sin día de la semana) */
  dayMonth: string;
  /** "enero 2026" (cabecera de calendario) */
  monthYear: string;
}

const dateFormats: Record<Lang, DateFormatSet> = {
  es: {
    long: "EEEE, d 'de' MMMM",
    short: "EEE d MMM",
    full: "EEEE, d 'de' MMMM 'de' yyyy",
    dayMonth: "d 'de' MMMM",
    monthYear: "MMMM yyyy",
  },
  en: {
    long: "EEEE, MMMM d",
    short: "EEE, MMM d",
    full: "EEEE, MMMM d, yyyy",
    dayMonth: "MMMM d",
    monthYear: "MMMM yyyy",
  },
  de: {
    long: "EEEE, d. MMMM",
    short: "EEE, d. MMM",
    full: "EEEE, d. MMMM yyyy",
    dayMonth: "d. MMMM",
    monthYear: "MMMM yyyy",
  },
  fr: {
    long: "EEEE d MMMM",
    short: "EEE d MMM",
    full: "EEEE d MMMM yyyy",
    dayMonth: "d MMMM",
    monthYear: "MMMM yyyy",
  },
  it: {
    long: "EEEE d MMMM",
    short: "EEE d MMM",
    full: "EEEE d MMMM yyyy",
    dayMonth: "d MMMM",
    monthYear: "MMMM yyyy",
  },
};

const capFirst = (value: string): string =>
  value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);

const interpolate = (
  template: string,
  vars?: Record<string, string | number>,
): string => {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );
};

/** "es-ES"/"es-MX" → "es", "en-US" → "en", resto (pt, ja, ...) → "es". */
const normalizeLang = (raw: string | undefined | null): Lang => {
  const primary = (raw || "").toLowerCase().split("-")[0];
  if (primary === "es" || primary === "en" || primary === "de" || primary === "fr" || primary === "it") {
    return primary;
  }
  return "es";
};

const VALID_LANGS: ReadonlyArray<Lang> = ["es", "en", "de", "fr", "it"];

/** Whitelist estricta: solo los 5 códigos exactos soportados, sin normalizar
 * variantes regionales (eso es cosa de `normalizeLang`, para navigator.language). */
const parseLangParam = (value: string | null): Lang | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  return (VALID_LANGS as ReadonlyArray<string>).includes(normalized)
    ? (normalized as Lang)
    : null;
};

/**
 * Prioridad de inicialización (de mayor a menor):
 * 1. `?lang=` de la URL — lo añade el agente de email a los enlaces de
 *    confirmación/gestión cuando la reserva no es en español (con "es" la
 *    URL va limpia). Si es válido, se usa Y se persiste, para que el resto
 *    de la sesión (y visitas futuras sin `?lang=`) recuerden la elección.
 * 2. `localStorage` (visita repetida sin `?lang=` en la URL).
 * 3. `navigator.language` del navegador.
 * 4. "es" (fallback final, dentro de `normalizeLang`).
 */
const detectInitialLang = (): Lang => {
  try {
    const fromQuery = parseLangParam(
      new URLSearchParams(window.location.search).get("lang"),
    );
    if (fromQuery) {
      try {
        window.localStorage.setItem(STORAGE_KEY, fromQuery);
      } catch {
        // Sin storage disponible: el idioma del enlace se aplica igualmente
        // en esta visita, solo no persiste para la siguiente.
      }
      return fromQuery;
    }
  } catch {
    // `window.location`/`URLSearchParams` no disponibles (entorno atípico);
    // se ignora y se sigue con el resto de la cadena de prioridad.
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) return normalizeLang(stored);
  } catch {
    // localStorage puede no estar disponible (iframe de terceros con
    // restricciones de cookies/storage); se ignora y se cae al navegador.
  }
  return normalizeLang(typeof navigator !== "undefined" ? navigator.language : undefined);
};

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => detectInitialLang());

  const setLang = (next: Lang) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ver comentario de detectInitialLang: si no hay storage, el idioma
      // simplemente no persiste entre visitas, pero la sesión actual sigue
      // funcionando con normalidad.
    }
  };

  const value = useMemo(() => ({ lang, setLang }), [lang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage debe usarse dentro de <LanguageProvider>");
  }
  return ctx;
}

/**
 * Hook principal de traducción. `t(key, vars)` para textos sueltos,
 * `tPeople`/`tPeopleShort` para el plural "1 persona"/"2 personas" (y
 * equivalentes en cada idioma), `formatDate` para fechas ya formateadas con
 * el locale y el patrón correctos (y la primera letra en mayúscula, sin
 * depender de `text-transform: capitalize` — en francés/italiano esa clase
 * CSS capitalizaría también el nombre del mes, que va en minúscula).
 */
export function useT() {
  const { lang, setLang } = useLanguage();
  const dict = messages[lang];

  const t = (key: keyof Messages, vars?: Record<string, string | number>): string => {
    const value = dict[key];
    return typeof value === "string" ? interpolate(value, vars) : "";
  };

  const tPeople = (n: number): string =>
    interpolate(n === 1 ? dict.commonPeopleOne : dict.commonPeopleOther, { n });

  const tPeopleShort = (n: number): string =>
    interpolate(
      n === 1 ? dict.commonPeopleShortOne : dict.commonPeopleShortOther,
      { n },
    );

  /**
   * Divide una cadena traducida en [antes, después] de `{varName}`, para
   * poder envolver el valor interpolado en JSX (p.ej. `<strong>`) sin perder
   * la traducción del resto de la frase. Ej: `tSplit("appSuccessBodyConfirmed",
   * "name")` con la cadena "Tu reserva en {name} ha sido registrada." →
   * `["Tu reserva en ", " ha sido registrada."]`.
   */
  const tSplit = (key: keyof Messages, varName: string): [string, string] => {
    const raw = dict[key];
    const value = typeof raw === "string" ? raw : "";
    const marker = `{${varName}}`;
    const idx = value.indexOf(marker);
    if (idx === -1) return [value, ""];
    return [value.slice(0, idx), value.slice(idx + marker.length)];
  };

  const formatDate = (date: Date, pattern: keyof DateFormatSet): string =>
    capFirst(
      format(date, dateFormats[lang][pattern], { locale: dateLocales[lang] }),
    );

  const formatTime = (time: string): string => `${time}${dict.commonTimeSuffix}`;

  return {
    lang,
    setLang,
    t,
    tPeople,
    tPeopleShort,
    tSplit,
    formatDate,
    formatTime,
    dateLocale: dateLocales[lang],
    weekdays: dict.commonCalendarWeekdays,
  };
}
